<template>
  <div v-if="hideNav">
    <!-- Nav is hidden -->
  </div>
  <div v-else-if="!computedNavItems || computedNavItems.length === 0" class="top-0 h-full  bg-white text-black p-5 z-0  w-64">
    <!-- No nav items to show -->
    <p class="text-sm text-gray-500">No navigation items</p>
  </div>
  <div v-else class="top-0 h-full  bg-white text-black p-5 z-0  w-64 flex flex-col justify-between">
    <div>
      <p class="text-3xl font-bold pt-5 pl-5">
        {{ computedNavTitle }}
      </p>
      <ul class="pt-3 text-center items-center pl-5">
        <template v-for="item in computedNavItems" :key="item.path || item.name">
          <li v-if="userHasAccess(item)">
            <a :href="getItemHref(item)" class="no-underline" :class="{'font-bold': isItemActive(item)}">
              <div class="flex items-center text-left py-2">
                <p>{{ item.title }}</p>
              </div>
            </a>
          </li>
        </template>
      </ul>
    </div>
    <router-link v-if="backToData" v-slot="{ href }" :to="{name: backToData.pathName}">
      <a :href="href">
        <div class="pl-5">
          <span class="text-sm">Back to</span><br>
          <span class="text-xl">{{ backToData.title }}</span>
        </div>
      </a>
    </router-link>
  </div>
</template>

<script>
  import { useAuthStore } from '../stores/auth';
  import { routes } from '../routing/index';

  export default {
    parent: true,

    data() {
      return {
        navigationItems: routes,
        auth: null,
      };
    },

    computed: {
      hasAnyRole() {
        if (!this.auth) return () => false;
        return this.auth.hasAnyRole;
      },

      currentNavItemList() {
        console.log('=== currentNavItemList DEBUG ===');
        console.log('Current route path:', this.$route.path);
        console.log('Current route name:', this.$route.name);
        console.log('Navigation items:', this.navigationItems);
        
        const found = this.navigationItems.find((x) => {
          console.log(`Checking navigation item: ${x.title} (path: ${x.path}, name: ${x.name})`);
          
          // Direct path match
          if (x.path === this.$route.path) {
            console.log(`✓ Found direct path match: ${x.title} (${x.path})`);
            return true;
          }
          
          // Check if current route name matches any child route names
          if (x.children && this.$route.name) {
            const childMatch = x.children.find((y) => y.name === this.$route.name);
            if (childMatch) {
              console.log(`✓ Found child name match: ${x.title} -> ${childMatch.title} (${childMatch.name})`);
              return true;
            }
          }
          
          // Check if current route path matches any child route paths
          if (x.children) {
            const childPathMatch = x.children.find((y) => y.path === this.$route.path);
            if (childPathMatch) {
              console.log(`✓ Found child path match: ${x.title} -> ${childPathMatch.title} (${childPathMatch.path})`);
              return true;
            }
          }
          
          // Check if current path starts with parent path (for nested routes)
          if (x.path && this.$route.path.startsWith(x.path)) {
            console.log(`✓ Found path prefix match: ${x.title} (${x.path} matches prefix of ${this.$route.path})`);
            return true;
          }
          
          console.log(`✗ No match for: ${x.title}`);
          return false;
        });
        
        console.log(`currentNavItemList result: ${found ? `${found.title} (${found.path})` : 'null'}`);
        return found;
      },

      hideNav() {
        return this.$route.meta.hideNav || false;
      },

      computedNavTitle() {
        // If the route has a custom title in meta, use it
        if (this.$route.meta && this.$route.meta.customTitle) {
          return this.$route.meta.customTitle;
        }
        
        // Otherwise use the title from the current nav item
        if (this.currentNavItemList) {
          return this.currentNavItemList.title;
        }
        
        return '';
      },

      computedNavItems() {
        console.log('=== computedNavItems DEBUG ===');
        console.log(`Current route: ${this.$route.name} (${this.$route.path})`);
        console.log('Route meta:', this.$route.meta);
        
        // If we have custom nav items in the route meta, use those
        if (this.$route.meta && this.$route.meta.customNavChildItems) {
          console.log(`Using customNavChildItems (${this.$route.meta.customNavChildItems.length} items):`, this.$route.meta.customNavChildItems.map(item => `${item.title} (${item.name})`));
          const filtered = this.$route.meta.customNavChildItems.filter(item => !item.hideFromNav);
          console.log(`Filtered customNavChildItems (${filtered.length} items):`, filtered.map(item => `${item.title} (${item.name})`));
          return filtered;
        }
        
        // Otherwise use the children of the current nav item
        if (this.currentNavItemList && this.currentNavItemList.children) {
          console.log(`Using currentNavItemList.children (${this.currentNavItemList.children.length} items):`, this.currentNavItemList.children.map(item => `${item.title} (${item.name || item.path})`));
          const filtered = this.currentNavItemList.children.filter(item => !item.hideFromNav);
          console.log(`Filtered children (${filtered.length} items):`, filtered.map(item => `${item.title} (${item.name || item.path})`));
          return filtered;
        }
        
        console.log('No nav items found, returning empty array');
        return [];
      },

      backToData() {
        console.log('=== backToData DEBUG ===');
        console.log(`Current route: ${this.$route.name} (${this.$route.path})`);
        console.log('Route meta:', this.$route.meta);
        
        // Check route meta for backToData
        if (this.$route.meta && this.$route.meta.backToData) {
          console.log(`✓ Found backToData: ${this.$route.meta.backToData.title} -> ${this.$route.meta.backToData.pathName}`);
          return this.$route.meta.backToData;
        }
        
        console.log('✗ No backToData found');
        return null;
      },
    },

    methods: {
      userHasAccess(item) {
        const noClaims = !item.claims || item.claims.length === 0;
        const noRoles = !item.roles || item.roles.length === 0;

        if (noClaims && noRoles) {
          return true;
        }

        if (item.claims && this.auth?.hasAnyClaim) {
          if (this.auth.hasAnyClaim(item.claims)) {
            return true;
          }
        }

        if (item.roles && this.auth?.hasAnyRole) {
          if (this.auth.hasAnyRole(item.roles)) {
            return true;
          }
        }

        return false;
      },

      isCurrentPath(path) {
        // Get the current path from the window location
        const currentPath = window.location.pathname;
        
        // Check if currentPath starts with the navigation path
        if (path === '/') {
          return currentPath === '/';
        }
        
        return currentPath.startsWith(path);
      },
      
      isItemActive(item) {
        // Check if the current route matches this item's name or path
        if (this.$route.name === item.name) {
          return true;
        }
        
        if (item.path && this.isCurrentPath(item.path)) {
          return true;
        }
        
        return false;
      },
      
      getItemHref(item) {
        try {
          // Get the resolved URL from the router
          const resolved = this.$router.resolve({ name: item.name });
          return resolved.href;
        } catch (error) {
          console.warn('Could not resolve route for item:', item, error);
          // Fallback to a simple path if route resolution fails
          return item.path || '#';
        }
      },
    },

    created() {
      this.auth = useAuthStore();
    },
  };
</script>